import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Button } from '@components/ui/button';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import { Checkbox } from '@components/ui/checkbox';
import { createRegistration } from '@generated/server/registrations';
import { checkCouponAvailability } from '@generated/server/coupons';
import { CheckCircle2, Loader2, AlertCircle, Music, Ticket } from 'lucide-react';
import { Alert, AlertDescription } from '@components/ui/alert';

export const Route = createFileRoute('/_app/')({ component: RegistrationPage });

function RegistrationPage() {
  const [couponsAvailable, setCouponsAvailable] = useState<boolean | null>(null);
  const [checkingAvailability, setCheckingAvailability] = useState(true);

  useEffect(() => {
    let active = true;
    checkCouponAvailability()
      .then((res) => { if (active) setCouponsAvailable(res?.available ?? false); })
      .catch(() => { if (active) setCouponsAvailable(false); })
      .finally(() => { if (active) setCheckingAvailability(false); });
    return () => { active = false; };
  }, []);

  if (checkingAvailability) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--muted-foreground))]" />
      </div>
    );
  }

  if (!couponsAvailable) {
    return <SoldOutView />;
  }

  return <RegistrationForm />;
}

function SoldOutView() {
  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
      <div className="mx-auto max-w-2xl w-full">
        <div className="rounded-[20px] bg-[hsl(var(--card))] p-8">
          <div className="text-center" dir="rtl">
            <img
              src="https://files-public.monday.com/euc1/6bea05a1-9a9b-496e-b02a-0dc49cce46e1/logo.png"
              alt="לוגו עיריית קריית שמונה"
              className="mx-auto mb-6 h-16 w-auto"
            />
            <h1 className="font-heading text-4xl font-bold tracking-tight text-[hsl(var(--card-foreground))]">
              הכרטיסים נגמרו!
            </h1>
            <div className="mx-auto mt-6 max-w-lg space-y-4">
              <p className="text-base leading-relaxed text-[hsl(var(--card-foreground))]">
                תודה לכולם על ההיענות המדהימה — 1,000 הכרטיסים הנוספים אזלו במהירות שיא.
              </p>
              <p className="text-sm leading-relaxed text-[hsl(var(--card-foreground)/0.85)]">
                לצערנו לא נוכל לאפשר כניסה למי שאינו מחזיק בכרטיס, וזאת מטעמי ארגון ובטיחות. נבקש להימנע מהגעה למי שטרם קיבל כרטיס, ומודים לכם על ההבנה.
              </p>
              <div className="mx-auto mt-6 rounded-xl bg-[hsl(var(--background))] px-5 py-4">
                <p className="text-base font-medium text-[hsl(var(--foreground))]">
                  נתראה הערב באירוע! 🎶
                </p>
                <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
                  פסטיבל פארק הזהב, קריית שמונה
                </p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  שערים: 19:00 | מופע: 20:00
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RegistrationForm() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    idNumber: '',
    phone: '',
    phoneConfirm: '',
    email: '',
    street: '',
    houseNumber: '',
    entrance: '',
    zipCode: '',
  });

  const [agreements, setAgreements] = useState({
    resident: false,
    terms: false,
    sms: false,
    marketing: false,
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [couponCode, setCouponCode] = useState<string | null>(null);
  const [error, setError] = useState('');

  const validateIdNumber = (id: string): boolean => {
    if (!id || !/^\d{8,9}$/.test(id)) return false;
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateIdNumber(formData.idNumber)) {
      setError('תעודת זהות לא תקינה');
      return;
    }

    if (formData.phone !== formData.phoneConfirm) {
      setError('מספרי הטלפון אינם זהים');
      return;
    }

    if (!agreements.resident || !agreements.terms || !agreements.sms) {
      setError('יש לאשר את כל ההצהרות החובה');
      return;
    }

    setLoading(true);

    try {
      const result = await createRegistration({
        data: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          idNumber: formData.idNumber,
          phone: formData.phone,
          email: formData.email,
          street: formData.street,
          houseNumber: parseInt(formData.houseNumber),
          entrance: formData.entrance,
          zipCode: formData.zipCode,
          show: 'רינת בר ובן אל תבורי',
        },
      });

      setCouponCode(result?.couponCode ?? null);
      setSuccess(true);
    } catch (err: any) {
      console.error('Registration error:', err);
      if (err?.message?.includes('NO_COUPONS_AVAILABLE')) {
        setError('הכרטיסים אזלו! לא ניתן להירשם כרגע.');
      } else if (err?.message?.includes('DUPLICATE_REGISTRATION')) {
        setError('תעודת זהות זו כבר רשומה במערכת. לא ניתן להירשם יותר מפעם אחת.');
      } else if (err?.message?.includes('USER_UNAUTHORIZED')) {
        setError('שגיאת הרשאות - אין גישה ליצירת רישומים. אנא פנה למנהל המערכת.');
      } else {
        setError('אירעה שגיאה בשליחת הטופס. אנא נסה שוב.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-[20px] bg-[hsl(var(--popover))] p-8 text-center" dir="rtl">
          <img
            src="https://files-public.monday.com/euc1/6bea05a1-9a9b-496e-b02a-0dc49cce46e1/logo.png"
            alt="לוגו עיריית קריית שמונה"
            className="mx-auto mb-4 h-12 w-auto"
          />
          <CheckCircle2 className="mx-auto h-16 w-16 text-[hsl(var(--chart-1))]" />
          <h2 className="mt-4 font-heading text-2xl font-bold text-[hsl(var(--foreground))]">
            תודה! הרישום נקלט במערכת
          </h2>

          {couponCode && (
            <div className="mt-4 rounded-xl bg-[hsl(var(--background))] p-4 border border-[hsl(var(--border))]">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Ticket className="h-5 w-5 text-[hsl(var(--accent))]" />
                <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">קוד הקופון שלך:</span>
              </div>
              <p className="font-mono text-2xl font-bold text-[hsl(var(--accent))]">
                {couponCode}
              </p>
            </div>
          )}

          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">
            {couponCode
              ? 'שמור את קוד הקופון! ניתן להשתמש בו לרכישת כרטיס בהנחה.'
              : 'הקופונים יישלחו בהודעת SMS למספר ' + formData.phone + ' תוך 72 שעות.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] py-12 px-4">
      <div className="mx-auto max-w-2xl">
        <div className="rounded-[20px] bg-[hsl(var(--card))] p-8">
          {/* Header with logo */}
          <div className="mb-10 text-center" dir="rtl">
            <img
              src="https://files-public.monday.com/euc1/6bea05a1-9a9b-496e-b02a-0dc49cce46e1/logo.png"
              alt="לוגו עיריית קריית שמונה"
              className="mx-auto mb-6 h-16 w-auto"
            />
            <h1 className="font-heading text-4xl font-bold tracking-tight text-[hsl(var(--card-foreground))]">
              הרשמה לכרטיס מסובסד
            </h1>
            <div className="mx-auto mt-5 max-w-lg rounded-xl bg-[hsl(var(--background))] px-5 py-4">
              <p className="text-sm leading-relaxed text-[hsl(var(--foreground))]">
                לאור הביקוש הגבוה למופע הערב של אושר כהן בפסטיבל פארק הזהב, פעלה עיריית קריית שמונה מול משטרת ישראל, בשיתוף משרד הנגב והגליל, ומודיעה על שחרור 1,000 כרטיסים מסובסדים נוספים למופע — על בסיס &quot;כל הקודם זוכה&quot;
              </p>
              <p className="mt-2 text-xs font-medium text-[hsl(var(--accent))]">
                קוד קופון יופיע לאחר סיום ההרשמה
              </p>
            </div>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-8" dir="rtl">
            {/* Show Selection - Fixed to אושר כהן */}
            <div className="rounded-[12px] bg-[hsl(var(--popover))] p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Music className="h-5 w-5 text-[hsl(var(--accent))]" />
                <h2 className="font-heading text-lg font-semibold text-[hsl(var(--foreground))]">
                  הופעה
                </h2>
              </div>
              <div className="relative rounded-xl border-2 border-[hsl(var(--accent))] bg-[hsl(var(--accent)/0.08)] px-5 py-4 text-center shadow-sm">
                <span className="block text-base font-semibold text-[hsl(var(--accent))]">
                  רינת בר ובן אל תבורי
                </span>
                <span className="absolute -top-2 -left-2 flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--accent))] text-[10px] text-white">
                  ✓
                </span>
              </div>
            </div>

            <div className="rounded-[12px] bg-[hsl(var(--popover))] p-6 space-y-4">
              <h2 className="font-heading text-lg font-semibold text-[hsl(var(--foreground))]">
                פרטי הנרשם
              </h2>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">שם פרטי *</Label>
                  <Input
                    id="firstName"
                    required
                    minLength={2}
                    maxLength={20}
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="rounded-[10px] text-right"
                    dir="rtl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">שם משפחה *</Label>
                  <Input
                    id="lastName"
                    required
                    minLength={2}
                    maxLength={20}
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="rounded-[10px] text-right"
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="idNumber">תעודת זהות *</Label>
                <Input
                  id="idNumber"
                  required
                  pattern="\d{8,9}"
                  maxLength={9}
                  value={formData.idNumber}
                  onChange={(e) => setFormData({ ...formData, idNumber: e.target.value })}
                  className="rounded-[10px] text-right"
                  dir="rtl"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="phone">טלפון נייד *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    pattern="05\d{8}"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="rounded-[10px] text-right"
                    dir="rtl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phoneConfirm">אימות טלפון נייד *</Label>
                  <Input
                    id="phoneConfirm"
                    type="tel"
                    required
                    pattern="05\d{8}"
                    value={formData.phoneConfirm}
                    onChange={(e) => setFormData({ ...formData, phoneConfirm: e.target.value })}
                    className="rounded-[10px] text-right"
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">דואר אלקטרוני</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="rounded-[10px] text-left"
                  dir="ltr"
                />
              </div>
            </div>

            <div className="rounded-[12px] bg-[hsl(var(--popover))] p-6 space-y-4">
              <h2 className="font-heading text-lg font-semibold text-[hsl(var(--foreground))]">
                כתובת מגורים
              </h2>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="street">רחוב *</Label>
                  <Input
                    id="street"
                    required
                    value={formData.street}
                    onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                    className="rounded-[10px] text-right"
                    dir="rtl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="houseNumber">מספר בית *</Label>
                  <Input
                    id="houseNumber"
                    type="number"
                    required
                    min={1}
                    value={formData.houseNumber}
                    onChange={(e) => setFormData({ ...formData, houseNumber: e.target.value })}
                    className="rounded-[10px] text-right"
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="entrance">כניסה / דירה</Label>
                  <Input
                    id="entrance"
                    value={formData.entrance}
                    onChange={(e) => setFormData({ ...formData, entrance: e.target.value })}
                    className="rounded-[10px] text-right"
                    dir="rtl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="zipCode">מיקוד</Label>
                  <Input
                    id="zipCode"
                    value={formData.zipCode}
                    onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                    className="rounded-[10px] text-right"
                    dir="rtl"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-[12px] bg-[hsl(var(--popover))] p-6 space-y-4">
              <h2 className="font-heading text-lg font-semibold text-[hsl(var(--foreground))]">
                הצהרות ואישורים
              </h2>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="resident"
                    checked={agreements.resident}
                    onCheckedChange={(v) => setAgreements({ ...agreements, resident: v === true })}
                  />
                  <Label htmlFor="resident" className="text-sm leading-relaxed cursor-pointer">
                    אני מצהיר/ה כי אני תושב/ת קריית שמונה *
                  </Label>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms"
                    checked={agreements.terms}
                    onCheckedChange={(v) => setAgreements({ ...agreements, terms: v === true })}
                  />
                  <Label htmlFor="terms" className="text-sm leading-relaxed cursor-pointer">
                    אני מאשר/ת את תנאי השימוש ומדיניות הפרטיות *
                  </Label>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="sms"
                    checked={agreements.sms}
                    onCheckedChange={(v) => setAgreements({ ...agreements, sms: v === true })}
                  />
                  <Label htmlFor="sms" className="text-sm leading-relaxed cursor-pointer">
                    אני מאשר/ת קבלת הודעות SMS בנוגע לכרטיסים *
                  </Label>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="marketing"
                    checked={agreements.marketing}
                    onCheckedChange={(v) => setAgreements({ ...agreements, marketing: v === true })}
                  />
                  <Label htmlFor="marketing" className="text-sm leading-relaxed cursor-pointer">
                    אני מאשר/ת קבלת עדכונים ודיוור מהעירייה
                  </Label>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full py-6 text-lg font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="ml-2 h-5 w-5 animate-spin" />
                  שולח...
                </>
              ) : (
                'שליחת הרשמה'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
